import React, { useRef } from "react";

import { renderBlockDescriptionHtml, renderBlockTitleHtml } from "./blockDescriptionHtml";
import { DashboardWindow } from "./dashboardHelpers";
import { useTheme } from "./themes";
import { ErrorStateDetails, useBlockRender } from "./useBlockRender";
import { SlicerState } from "./useDashboardRuntime";

interface Props {
  blockId: string;
  title: string;
  description?: string | null;
  renderFunctionJs: string;
  slicerState: SlicerState;
  targetWindow: DashboardWindow;
  idSuffix?: string;
  blockType: string;
  // BE-provided pre-render error attribution (e.g. missing source data). When
  // set, the error fallback shows this string verbatim in place of the generic
  // "Unable to display table" title and hides the "Learn more" button.
  errorState?: string | null;
  // Companion to `errorState`: open dict of structured attribution details
  // (currently `{ tabs?: string[] }` for source tabs, rendered as a tooltip).
  errorStateDetails?: ErrorStateDetails | null;
  onRendered?: (blockId: string) => void;
  onRenderStart?: (blockId: string) => void;
  onRenderComplete?: (blockId: string) => void;
  onFixBlock?: (blockId: string, errorMessage: string) => void;
  /** Wired to the "Learn more" button in the error fallback. */
  onExplainBlockInChat?: (
    blockId: string,
    errorMessage?: string,
    errorStateDetails?: ErrorStateDetails | null
  ) => void;
}

export const DataTableBlock: React.FC<Props> = ({
  blockId,
  title,
  description,
  renderFunctionJs,
  slicerState,
  targetWindow,
  idSuffix = "",
  blockType,
  errorState,
  errorStateDetails,
  onRendered,
  onRenderStart,
  onRenderComplete,
  onFixBlock,
  onExplainBlockInChat,
}) => {
  const { themeSignature } = useTheme();
  const cardRef = useRef<HTMLDivElement>(null);

  const fullBlockId = `block_${blockId}${idSuffix ? `_${idSuffix}` : ""}`;

  const escapedTitle = title.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const escapedDescription = description
    ? description.replace(/</g, "&lt;").replace(/>/g, "&gt;")
    : null;

  useBlockRender({
    cardRef,
    // The desktop reservation (search input width + actions offset, ~300px) keeps the title clear
    // of the search/actions overlay that sits on the title line.
    //
    // Mobile needs no reservation at all: the search reflows to a pre-expanded full-width bar
    // below the title there (see the `.data-table-container` mobile block in applyTheme.ts) and
    // the action buttons never render (showActionsInTable is false for in-dashboard blocks), so
    // the title row owns the full card width. The description's info icon then sits inline to the
    // right of the title per Figma 5753:13777. The reservation therefore rides on renderBlockTitleHtml's
    // desktop-only copy of the h2, which is also why it no longer needs a `max-sm:!mr-0` counterpart.
    initialInnerHtml: `<div class="block-title-row max-sm:relative max-sm:flex max-sm:items-center max-sm:gap-[8px] max-sm:pb-[12px]">${renderBlockTitleHtml(
      escapedTitle,
      fullBlockId,
      "mr-[calc(var(--data-table-search-width)+var(--data-table-actions-offset))]"
    )}${
      escapedDescription ? renderBlockDescriptionHtml(escapedDescription, fullBlockId) : ""
    }</div><div class="data-table-container flex-1 min-h-0 ag-theme-material" data-sentry-mask></div>`,
    renderFunctionJs,
    slicerState,
    targetWindow,
    fullBlockId,
    blockId,
    blockType,
    blockTitle: title,
    errorState,
    errorStateDetails,
    themeSignature,
    onRendered,
    onRenderStart,
    onRenderComplete,
    onFixBlock,
    onExplainBlockInChat,
  });

  return (
    <div
      ref={cardRef}
      id={fullBlockId}
      data-coeff-block-id={blockId}
      className="themed-dashboard-card"
    />
  );
};
